// CreateVideoToolUI — branded tool-UI for the agent's `createVideo` tool call,
// rendered inline in the Aidea Assistant chat thread.
//
// The tool result is `{ feature:'video', prompt, settings?, openPopup:true }`.
// On the COMPLETE state we dispatch ONCE (useRef guard) via dispatchOpenAIPopup
// to open VideoModal pre-filled with the script + any provider/aspect/duration
// hints. The popup owns style pick + generation + add-to-scene. Clone of the
// AddNarration/CreateAvatar OpenedState pattern.
//
// Registration: makeAssistantToolUI({ toolName: "createVideo", render }).

import React, { useCallback, useEffect, useRef } from "react";
import { makeAssistantToolUI } from "@assistant-ui/react";

import { SpinnerIcon, VideoIcon, AlertIcon } from "../icons";
import { dispatchOpenAIPopup, shouldAutoOpenOnce } from "../aiPopupGateway";
import { OpenPopupButton } from "./OpenPopupButton";
import { mapAiProviderErrorMessage } from "../../../../common/ai_provider_errors.es6";

// Parse the tool result into { prompt, settings, openPopup } | { error } | null.
function parseVideoResult(result) {
  if (result == null) return null;
  let data = result;
  if (typeof result === "string") {
    const trimmed = result.trim();
    if (!trimmed) return null;
    try {
      data = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== "object") return null;
  if (typeof data.error === "string" && data.error.trim()) {
    return { error: data.error.trim() };
  }
  const openPopup = data.openPopup === true;
  const prompt = typeof data.prompt === "string" ? data.prompt.trim() : "";
  const settings = data.settings && typeof data.settings === "object" ? data.settings : {};
  if (!openPopup && !prompt) return null;
  return { prompt, settings, openPopup };
}

function HeaderChip({ label }) {
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-[var(--aidea-primary-tint)] tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-[var(--aidea-primary)]">
      <VideoIcon size={12} />
      {label}
    </span>
  );
}

function Card({ children }) {
  return (
    <div className="tw-mt-2 tw-w-full tw-max-w-[440px] aidea-chat-card tw-overflow-hidden tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)]">
      {children}
    </div>
  );
}

function RunningState() {
  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-3">
        <SpinnerIcon size={15} className="aidea-spin tw-flex-none tw-text-[var(--aidea-primary)]" />
        <span className="tw-min-w-0 tw-truncate tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
          Preparing the video script…
        </span>
      </div>
    </Card>
  );
}

function ErrorState({ message }) {
  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip label="Video" />
      </div>
      <div className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-3 tw-text-[13px] tw-text-[var(--aidea-muted-fg)]">
        <AlertIcon size={15} className="tw-mt-0.5 tw-flex-none tw-text-[var(--aidea-muted-fg)]" />
        <span className="tw-min-w-0">
          {mapAiProviderErrorMessage(message) || message || "Couldn't open the video generator."}
        </span>
      </div>
    </Card>
  );
}

// Dispatches via the gateway exactly once (useRef guard), then shows a short
// branded confirmation. VideoModal owns style pick + generation + add-to-scene.
function OpenedState({ prompt, settings, toolCallId }) {
  const dispatchedRef = useRef(false);

  const doOpen = useCallback(
    () => dispatchOpenAIPopup({ feature: "video", prompt, settings }),
    [prompt, settings]
  );

  // Auto-open ONCE per tool call (suppress re-open on chat close/reopen + restore).
  useEffect(() => {
    if (dispatchedRef.current) return;
    dispatchedRef.current = true;
    const key = toolCallId || `video:${prompt || ""}`;
    if (!shouldAutoOpenOnce(key)) return undefined;
    const cancel = doOpen();
    return cancel;
  }, [doOpen, toolCallId, prompt]);

  const aspect = settings && settings.aspectRatio ? ` · ${settings.aspectRatio}` : "";

  return (
    <Card>
      <div className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-pt-2.5">
        <HeaderChip label="Video" />
      </div>
      <div className="tw-flex tw-items-start tw-gap-2.5 tw-px-3 tw-py-3">
        <span className="tw-flex tw-h-8 tw-w-8 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-[var(--aidea-primary-tint)] tw-text-[var(--aidea-primary)]">
          <VideoIcon size={16} />
        </span>
        <div className="tw-min-w-0">
          <p className="tw-m-0 tw-text-[13px] tw-leading-snug tw-text-[var(--aidea-fg)]">
            {`Opened the video generator${aspect} with the script. Pick a style and model, then Generate.`}
          </p>
          <OpenPopupButton label="Open video generator" onClick={doOpen} />
        </div>
      </div>
    </Card>
  );
}

export const CreateVideoToolUI = makeAssistantToolUI({
  toolName: "createVideo",
  render: ({ result, status, toolCallId }) => {
    if (status && status.type === "running") {
      return <RunningState />;
    }
    const parsed = parseVideoResult(result);
    if (!parsed) {
      return <ErrorState />;
    }
    if (parsed.error) {
      return <ErrorState message={parsed.error} />;
    }
    return (
      <OpenedState
        prompt={parsed.prompt || ""}
        settings={parsed.settings || {}}
        toolCallId={toolCallId}
      />
    );
  },
});

export default CreateVideoToolUI;
